import { createGlobalStyle } from 'styled-components';
import 'react-toastify/dist/ReactToastify.css';
import reactDatePickerStyles from './reactDatePickerStyles';
import 'reactflow/dist/style.css';
import { theme } from './theme';
import { floatingComponentStyles } from './shareableStyles';

const GlobalStyles = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
    font-family: 'Geist', 'Helvetica Neue', sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    font-weight: 400;
    font-size: 12px;
    line-height: normal;
    overflow: hidden;
    
    @media print {
      overflow-y: auto;
      overflow-x: hidden;
    }
    
    background-color: #f3f5f7;
    color: #222;
    --color-level-0: #f3f5f7;
    --color-level-1: #fcfcfc;
    --color-level-2: #fff;
    --color-inverse: #fff;
    --color-border: #e8eaf2;
    --color-table-header: #f7f8fe;
    --color-content-highlight: #f2f7fd;
    --color-main-blue: #006bd3;
    --color-blue-900: #343a46;
    --color-blue-500: #e5f0fb;
    --color-secondary-900: #222;
    --color-secondary-800: #555;
    --color-secondary-600: #777;
    --color-secondary-400: #bbb;
    --color-secondary-300: #e5e5e5;
    --shadow-1: 0 4px 8px 0 rgba(0, 0, 0, 0.08);
    --shadow-2: 0 4px 28px 0 rgba(0, 0, 0, 0.14);
    --shadow-3: 0 4px 32px 0 rgba(0, 0, 0, 0.28);

    --grayscale-100: #f8f8f8;
    --grayscale-200: #eeeeee;
    --grayscale-300: #dcdcdd;
    --grayscale-400: #c2c8cb;
    --grayscale-500: #a2aaaf;
    --grayscale-600: #767c87;
    --grayscale-700: #525966;
    --grayscale-800: #343942;
    --grayscale-900: #19202b;
    --grayscale-1000: #111111;
    --green-100: #ccffea;
    --green-200: #99ffe7;
    --green-300: #00d09e;
    --green-400: #00ad88;
    --green-500: #008e64;
    --green-600: #007457;
    --green-700: #005c45;
    --green-800: #004634;
    --green-900: #003123;
    --purple-100: #e3d8ff;
    --purple-200: #cebaff;
    --purple-300: #ab8afb;
    --purple-400: #9569ff;
    --purple-500: #7b3efc;
    --purple-600: #662ce2;
    --purple-700: #5626bb;
    --purple-800: #421c91;
    --purple-900: #2e1561;
    --purple-1000: #1b0d39;
    --red-100: #ffe7e7;
    --red-200: #ffcdd0;
    --red-300: #ff8e96;
    --red-400: #ff424d;
    --red-500: #d8092f;
    --red-600: #b20625;
    --red-700: #90031c;
    --red-800: #6f0213;
    --red-900: #4d010a;
  }
  body.dark {
    background-color: #1c1c1c;
    color: #fff;
    --color-level-0: #1c1c1c;
    --color-level-1: #222;
    --color-level-2: #2a2a2a;
    --color-inverse: #1c1c1c;
    --color-border: #343434;
    --color-table-header: #212428;
    --color-content-highlight: #272a2d;
    --color-main-blue: #60a6ff;
    --color-blue-900: #262f3b;
    --color-blue-500: #333d48;
    --color-secondary-900: #fff;
    --color-secondary-800: #cacaca;
    --color-secondary-600: #aeaeae;
    --color-secondary-400: #777;
    --color-secondary-300: #555;
    --shadow-1: 0 4px 8px 0 rgba(0, 0, 0, 0.68);
    --shadow-2: 0 4px 28px 0 rgba(0, 0, 0, 0.84);
    --shadow-3: 0 4px 32px 0 #000;

    --grayscale-100: #111111;
    --grayscale-200: #19202b;
    --grayscale-300: #343942;
    --grayscale-400: #525966;
    --grayscale-500: #767c87;
    --grayscale-600: #a2aaaf;
    --grayscale-700: #c2c8cb;
    --grayscale-800: #dcdcdd;
    --grayscale-900: #eeeeeb;
    --grayscale-1000: #f8f8f8;
    --green-100: #003123;
    --green-200: #004634;
    --green-300: #004634;
    --green-400: #007457;
    --green-500: #008e64;
    --green-600: #00ad88;
    --green-700: #00d09e;
    --green-800: #99ffe7;
    --green-900: #ccffea;
    --purple-100: #1b0d39;
    --purple-200: #2e1561;
    --purple-300: #421c91;
    --purple-400: #662ce2;
    --purple-500: #9569ff;
    --purple-600: #7b3efc;
    --purple-700: #9569ff;
    --purple-800: #ab8afb;
    --purple-900: #cebaff;
    --purple-1000: #e3d8ff;
    --red-100: #4d010a;
    --red-200: #6f0213;
    --red-300: #90031c;
    --red-400: #b20625;
    --red-500: #d8092f;
    --red-600: #ff424d;
    --red-700: #ff8e96;
    --red-800: #ffcdd0;
    --red-900: #ffffff;
  }

  @media print {
    @page {
      size: 1920px 1483px; // letter landscape proportions 11/8.5 inches
    }
  }

  /* Remove all unwanted elements from embedded view */
  #root, .fluent-ui-theme-provider-element {
    height: 100%;
    background: unset;
    color: inherit;
    @media print {
      height: auto;
    }
  }

  .ui.modal {
    box-shadow: none !important;
    font-family: inherit;
    font-size: inherit;
    font-weight: inherit;
    background: unset;
    >.content {
      ${floatingComponentStyles};
    }
  }

  button {
    background: none;
    border-radius: 0;
    border: none;
    padding: 0;
  }

  *, *:after, *:before {
    box-sizing: border-box;
    //prevents flexbox children to expand beyond
    // the allocated space https://stackoverflow.com/a/38224283
    min-width: 0;
  }

  /* Handle body scroll while modal is open */
  .modal-open {
    overflow: hidden !important;
  }

  .modal-open .back-to-top, .dimmable.dimmed .back-to-top {
    display: none !important;
  }

  .recharts-reference-line-line {
    transform: scale(1, 0.96) translateY(4%);
  }

  .highlight-dot {
    cursor: pointer !important;
  }

  .highlight-dot>image {
    pointer-events: none !important;
  }

  .chart-loader .recharts-cartesian-axis.recharts-yAxis .recharts-cartesian-axis-ticks {
    visibility: hidden;
  }

  /* Remove the top line from cartesian grid */
  .recharts-surface .recharts-cartesian-grid .recharts-cartesian-grid-horizontal line[y1="15"][y2="15"] {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
    stroke: #fff !important;
    fill: #fff !important;
    fill-opacity: 0 !important;
  }

  .recharts-dot.recharts-reference-dot-dot {
    opacity: 0;
  }

  .recharts-tooltip-cursor {
    fill: rgba(229, 229, 229, 0.5);
  }

  .recharts-responsive-container {
    overflow: hidden;
  }

  .recharts-wrapper {
    position: static !important;
  }

  .ui.menu {
    border: none;
  }

  .ui.menu .ui.selection {
    border: 1px solid ${theme.colors.mainBlue} !important;
    color: ${theme.colors.mainBlue} !important;
    font-weight: 600 !important;
    font-size: 14px !important;
  }

  .ui.menu .ui.active.visible.selection {
    font-weight: 600 !important;
    font-size: 14px !important;
    color: ${theme.colors.mainBlue} !important;
  }

  .ui.menu .ui.active.visible.selection .item {
    font-weight: 500 !important;
    font-size: 14px !important;
    color: #999999 !important;
  }

  .ui.menu .ui.active.visible.selection .item.active {
    font-weight: 500 !important;
    font-size: 14px !important;
    color: ${theme.colors.mainBlue} !important;
  }

  /* ========================== */

  .chart-loader .ui.segment {
    border: none !important;
    -webkit-box-shadow: none !important;
    -moz-box-shadow: none !important;
    box-shadow: none !important;
    z-index: 10 !important;
  }

  /* Recolor recharts axis colors */
  .recharts-layer.recharts-cartesian-axis-tick>.recharts-xAxis,
  .recharts-layer.recharts-cartesian-axis.recharts-xAxis.xAxis>.recharts-xAxis,
  .recharts-cartesian-grid .recharts-cartesian-grid-horizontal>line,
  .recharts-text.recharts-cartesian-axis-tick-value,
  .recharts-cartesian-grid-vertical>line,
  line.recharts-yAxis {
    stroke: ${theme.colors.border};
  }

  /* Cropper.js modal when enter crop mode*/
  .cropper-drag-box {
    background-color: black !important;
    opacity: 0.5 !important;
  }

  /* Hack on prediction bar chart for tooltip position */
  .PredictionBarChart > div {
    top: 70px !important;
  }

  .PredictionPopper::before {
    display: none !important;
  }
  
  /* Popover in Formula page */
  .MaterialPopoverRoot > div:first-child {
    // backdrop styles
    background-color: rgba(0, 0, 0, 40%) !important;
  }

  .ui.multiple.search.dropdown>input.search {
    width: 0.1em;
  }
  
  // fluent-ui reset styles
  .ms-ContextualMenu-Callout {
    box-shadow: none !important;
  }
  .ms-ContextualMenu {
    font: inherit;
    color: inherit;
    ${floatingComponentStyles};
    box-shadow: ${theme.effects.shadow3};
  }
  .ms-ContextualMenu-item {
    font: inherit;
    color: inherit;
    outline: none;
  }
  /* KBT-1450: FluentUI will create a height 0 element on the position of <ContextualMenu /> */
  /* to avoid contextual menu impact the layout when we have display: flex with gaps */
  /* use display: contents to not join the flex layout */
  .ms-layer {
    display: contents; 
  }
  .ms-ContextualMenu-link {
    display: flex;
    align-items: center;
    column-gap: 8px;
    width: 100%;
    height: 30px;
    margin: 0;
    font: inherit;
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
    outline: none;
    color: ${theme.colors.secondary900};
    background-color: ${theme.colors.level2};
    &:hover,
    &.is-expanded {
      cursor: pointer;
      color: ${theme.colors.mainBlue};
      background-color: ${theme.colors.blue500};
    }
    &.is-disabled {
      pointer-events: auto;
      color: ${theme.colors.secondary400};
      &:hover {
        background-color: unset;
        color: ${theme.colors.secondary400};
      }
      &[aria-haspopup=true] {
        pointer-events: none;
      }
    }
    .ms-ContextualMenu-item:first-child & {
      border-top-left-radius: 4px;
      border-top-right-radius: 4px;
    }
    .ms-ContextualMenu-item:last-child & {
      border-bottom-left-radius: 4px;
      border-bottom-right-radius: 4px;
    }
  }
  .ms-ContextualMenu-linkContent {
    width: 100%;
  }
  .ms-ContextualMenu-itemText {
    margin: 0;
  }
  .ms-ContextualMenu-submenuIcon {
    flex: 0 0 auto;
    width: 20px;
    height: 16px;
    margin-left: auto;
    line-height: normal;
    color: inherit;
  }
  .ms-ContextualMenu-divider {
    background-color: ${theme.colors.border};
  }
  .ms-Callout-beak {
    ${floatingComponentStyles};
    border-radius: 0;
  }
  .ms-Callout-beakCurtain {
    background: unset;
    border-radius: unset;
  }

  // remove arrows in number input
  input[type="number"]::-webkit-outer-spin-button,
  input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type="number"] {
    -moz-appearance:textfield; /* Firefox */
  }

  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active{
    -webkit-background-clip: text;
    -webkit-text-fill-color: ${theme.colors.secondary900};
    transition: background-color 5000s ease-in-out 0s;
    box-shadow: inset 0 0 40px 40px #fff;
    body.dark & {
      box-shadow: inset 0 0 40px 40px ${theme.colors.level0};
    }
  }

  b,
  strong {
    font-weight: 600;
  }
  
  a::selection,
  input::selection,
  textarea::selection {
    background-color: ${theme.colors.grayscale400};
    color: ${theme.colors.grayscale1000};
  }
  
  ${reactDatePickerStyles}
`;

export default GlobalStyles;
